import React, { useState, useEffect, useCallback } from "react";
import { FaWarehouse, FaLayerGroup, FaTag, FaCalendarTimes, FaPlus,
         FaEdit, FaTrash, FaSave, FaTimes, FaChevronDown, FaChevronRight, FaMedal } from "react-icons/fa";
import {useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const LANE_TYPE_LABELS = { full_lane: "Full Lane", cage: "Cage", box: "Box" };
const LANE_TYPE_COLORS = { full_lane: "#6366f1", cage: "#0ea5e9", box: "#10b981" };
const BLOCK_TYPES = ["maintenance", "holiday", "academy_reserve", "closure", "other"];
const DURATIONS = [
  { label: "30 minutes", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "Half Day", minutes: 240 },
  { label: "Full Day", minutes: 480 },
];

function makeS(T) {
  return {
    card:  { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
             border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh,
             padding: "20px 24px", boxSizing: "border-box" },
    inp:   { padding: "10px 14px", borderRadius: 8, border: `1.5px solid ${T.inputBdr}`,
             background: T.inputBg, color: T.inputText, fontSize: 14, fontFamily: FONT, outline: "none" },
    btn:   { padding: "10px 20px", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700,
             cursor: "pointer", fontFamily: FONT },
    yBtn:  { padding: "10px 20px", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700,
             cursor: "pointer", color: getBrandColors().blueDark,
             background: "linear-gradient(135deg,#ffd700,#fff3a0)",
             boxShadow: "0 4px 14px rgba(255,200,0,0.32)", fontFamily: FONT },
    lbl:   { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
             color: T.textLabel, fontFamily: FONT, marginBottom: 6, display: "block" },
    tab:   (active) => ({
      padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13,
      fontWeight: 700, fontFamily: FONT,
      background: active ? "linear-gradient(135deg,#ffd700,#fff3a0)" : T.tabInBg,
      color: active ? getBrandColors().blueDark : T.tabInClr,
    }),
  };
}

// ── Facility Tab ───────────────────────────────────────────────────────────────
function FacilitiesTab({ T, S }) {
  const [list, setList]         = useState([]);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);

  const DAYS = ["weekday", "saturday", "sunday"];
  const DEFAULT_HOURS = { weekday: { open: "06:00", close: "22:00" },
                          saturday: { open: "08:00", close: "18:00" },
                          sunday:   { open: "09:00", close: "17:00" } };

  const load = useCallback(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json()).then(d => setList(d.facilities || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setForm({ facility_name: "", address: "", timezone: "Australia/Sydney",
              advance_booking_days: 7, hours: { ...DEFAULT_HOURS } });
    setEditing("new");
  };

  const startEdit = (f) => {
    let hours = { ...DEFAULT_HOURS };
    try { if (f.operating_hours_json) hours = { ...DEFAULT_HOURS, ...JSON.parse(f.operating_hours_json) }; }
    catch (_) {}
    setForm({ ...f, advance_booking_days: f.advance_booking_days ?? 7, hours });
    setEditing(f.facility_id);
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, operating_hours_json: JSON.stringify(form.hours) };
    delete payload.hours;
    const url = editing === "new" ? `${BASE}/lb/facilities` : `${BASE}/lb/facilities/${editing}`;
    const method = editing === "new" ? "POST" : "PUT";
    await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    setSaving(false); setEditing(null); load();
  };

  const setHour = (day, field, val) =>
    setForm(f => ({ ...f, hours: { ...f.hours, [day]: { ...f.hours[day], [field]: val } } }));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT }}>Facilities</div>
        <button style={S.yBtn} onClick={startNew}><FaPlus size={12} style={{ marginRight: 6 }} />Add Facility</button>
      </div>

      {editing && (
        <div style={{ ...S.card, marginBottom: 20, borderColor: "rgba(99,102,241,0.35)" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 16 }}>
            {editing === "new" ? "New Facility" : "Edit Facility"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            {[["Facility Name", "facility_name"], ["Address", "address"], ["Timezone", "timezone"]].map(([lbl, key]) => (
              <div key={key} style={key === "address" ? { gridColumn: "1/-1" } : {}}>
                <label style={S.lbl}>{lbl}</label>
                <input value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ ...S.inp, width: "100%" }} />
              </div>
            ))}
            <div>
              <label style={S.lbl}>Client Advance Booking (days)</label>
              <input type="number" min={1} max={365}
                value={form.advance_booking_days ?? 7}
                onChange={e => setForm(f => ({ ...f, advance_booking_days: Number(e.target.value) || 7 }))}
                style={{ ...S.inp, width: "100%" }} />
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 4 }}>
                Clients can only book within this many days from today (admins have no restriction)
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.textLabel, fontFamily: FONT, marginBottom: 10 }}>
            OPERATING HOURS
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
            {DAYS.map(day => (
              <div key={day} style={{ ...S.card, padding: "12px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.textLabel, fontFamily: FONT,
                              textTransform: "uppercase", marginBottom: 8 }}>{day}</div>
                {["open","close"].map(f => (
                  <div key={f} style={{ marginBottom: 6 }}>
                    <label style={{ ...S.lbl, fontSize: 10 }}>{f}</label>
                    <input type="time" value={(form.hours?.[day]?.[f]) || "06:00"}
                      onChange={e => setHour(day, f, e.target.value)}
                      style={{ ...S.inp, width: "100%", fontSize: 13 }} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.yBtn} onClick={save} disabled={saving}>
              <FaSave size={12} style={{ marginRight: 6 }} />{saving ? "Saving…" : "Save"}
            </button>
            <button style={{ ...S.btn, background: T.tabInBg, color: T.tabInClr }}
              onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {list.map(f => (
        <div key={f.facility_id} style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: FONT }}>{f.facility_name}</div>
              <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
                {f.address || "No address"} · {f.timezone}
              </div>
            </div>
            <button style={{ ...S.btn, background: T.tabInBg, color: T.tabInClr, padding: "7px 14px" }}
              onClick={() => startEdit(f)}>
              <FaEdit size={12} style={{ marginRight: 5 }} />Edit
            </button>
          </div>
        </div>
      ))}
      {!list.length && !editing && (
        <div style={{ ...S.card, textAlign: "center", color: T.textMuted, fontFamily: FONT, padding: 40 }}>
          No facilities yet. Add one to get started.
        </div>
      )}
    </div>
  );
}

// ── Lanes Tab ──────────────────────────────────────────────────────────────────
function LanesTab({ T, S }) {
  const [facilities, setFacilities] = useState([]);
  const [lanes,      setLanes]      = useState([]);
  const [selFac,     setSelFac]     = useState(null);
  const [form,       setForm]       = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [expanded,   setExpanded]   = useState({});

  const loadFacilities = useCallback(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json()).then(d => {
      const list = d.facilities || [];
      setFacilities(list);
      if (list.length && !selFac) setSelFac(list[0].facility_id);
    });
  }, [selFac]);

  const loadLanes = useCallback(() => {
    if (!selFac) return;
    fetch(`${BASE}/lb/lanes?facility_id=${selFac}`).then(r => r.json())
      .then(d => setLanes(d.lanes || []));
  }, [selFac]);

  useEffect(() => { loadFacilities(); }, [loadFacilities]);
  useEffect(() => { loadLanes(); }, [loadLanes]);

  const fullLanes = lanes.filter(l => l.lane_type === "full_lane");
  const cages     = lanes.filter(l => l.lane_type === "cage");
  const boxes     = lanes.filter(l => l.lane_type === "box");

  const startNew = (type, parent_lane_id = null) => {
    setForm({ facility_id: selFac, lane_name: "", lane_type: type,
              parent_lane_id, box_group_id: null, display_order: 0,
              color: LANE_TYPE_COLORS[type] || getBrandColors().blue, capacity: 1 });
  };

  const startEdit = (lane) => setForm({ ...lane });

  const save = async () => {
    setSaving(true);
    const url = form.lane_id ? `${BASE}/lb/lanes/${form.lane_id}` : `${BASE}/lb/lanes`;
    const method = form.lane_id ? "PUT" : "POST";
    await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSaving(false); setForm(null); loadLanes();
  };

  const deactivate = async (lane_id) => {
    if (!window.confirm("Deactivate this lane? Existing bookings are unaffected.")) return;
    await fetch(`${BASE}/lb/lanes/${lane_id}`, { method: "DELETE" });
    loadLanes();
  };

  const chip = (lane) => (
    <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase",
                   background: LANE_TYPE_COLORS[lane.lane_type] + "22",
                   color: LANE_TYPE_COLORS[lane.lane_type],
                   borderRadius: 6, padding: "2px 7px", marginLeft: 8 }}>
      {LANE_TYPE_LABELS[lane.lane_type]}
    </span>
  );

  const conflictNames = (lane) => {
    if (!lane.conflict_ids?.length) return null;
    const names = lane.conflict_ids.map(id => {
      const l = lanes.find(x => x.lane_id === Number(id));
      return l ? l.lane_name : id;
    }).join(", ");
    return <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}> conflicts: {names}</span>;
  };

  return (
    <div>
      {/* Facility selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={S.lbl}>Facility</label>
        <select value={selFac || ""} onChange={e => setSelFac(Number(e.target.value))}
          style={{ ...S.inp, width: 280 }}>
          {facilities.map(f => <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>)}
        </select>
      </div>

      {/* Form */}
      {form && (
        <div style={{ ...S.card, marginBottom: 20, borderColor: "rgba(99,102,241,0.35)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 14 }}>
            {form.lane_id ? "Edit Lane" : `Add ${LANE_TYPE_LABELS[form.lane_type]}`}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={S.lbl}>Name</label>
              <input value={form.lane_name} onChange={e => setForm(f => ({ ...f, lane_name: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} placeholder="e.g. Lane 1, Cage 1A" />
            </div>
            <div>
              <label style={S.lbl}>Color</label>
              <input type="color" value={form.color}
                onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                style={{ ...S.inp, padding: 4, width: "100%", height: 40 }} />
            </div>
            <div>
              <label style={S.lbl}>Order</label>
              <input type="number" value={form.display_order}
                onChange={e => setForm(f => ({ ...f, display_order: Number(e.target.value) }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
          </div>
          {form.lane_type === "cage" && (
            <div style={{ marginBottom: 14 }}>
              <label style={S.lbl}>Parent Full Lane</label>
              <select value={form.parent_lane_id || ""} style={{ ...S.inp, width: "100%" }}
                onChange={e => setForm(f => ({ ...f, parent_lane_id: Number(e.target.value) || null }))}>
                <option value="">— none —</option>
                {fullLanes.map(l => <option key={l.lane_id} value={l.lane_id}>{l.lane_name}</option>)}
              </select>
            </div>
          )}
          {form.lane_type === "box" && (
            <div style={{ marginBottom: 14 }}>
              <label style={S.lbl}>Box Group ID (number, shared by lanes in same box)</label>
              <input type="number" value={form.box_group_id || ""}
                onChange={e => setForm(f => ({ ...f, box_group_id: Number(e.target.value) || null }))}
                style={{ ...S.inp, width: 120 }} />
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.yBtn} onClick={save} disabled={saving}>
              <FaSave size={12} style={{ marginRight: 6 }} />{saving ? "Saving…" : "Save"}
            </button>
            <button style={{ ...S.btn, background: T.tabInBg, color: T.tabInClr }}
              onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Lane tree */}
      {selFac && (
        <div>
          {/* Full Lanes */}
          <div style={{ ...S.card, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: LANE_TYPE_COLORS.full_lane, fontFamily: FONT }}>
                Full Cricket Lanes
              </div>
              <button style={{ ...S.btn, background: LANE_TYPE_COLORS.full_lane + "18",
                               color: LANE_TYPE_COLORS.full_lane, fontSize: 12, padding: "6px 12px" }}
                onClick={() => startNew("full_lane")}>
                <FaPlus size={10} style={{ marginRight: 4 }} />Add Lane
              </button>
            </div>
            {fullLanes.map(lane => (
              <div key={lane.lane_id} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                              borderRadius: 8, background: T.inputBg }}>
                  <div style={{ width: 14, height: 14, borderRadius: 4, background: lane.color, flexShrink: 0 }} />
                  <button style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted,
                                   padding: 0, fontSize: 12 }}
                    onClick={() => setExpanded(e => ({ ...e, [lane.lane_id]: !e[lane.lane_id] }))}>
                    {expanded[lane.lane_id] ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                  </button>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT }}>
                    {lane.lane_name}
                  </span>
                  {chip(lane)}
                  {conflictNames(lane)}
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                     background: T.tabInBg, color: T.tabInClr }}
                      onClick={() => startEdit(lane)}><FaEdit size={10} /></button>
                    <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                     background: "rgba(239,68,68,0.12)", color: "#ef4444" }}
                      onClick={() => deactivate(lane.lane_id)}><FaTrash size={10} /></button>
                  </div>
                </div>
                {expanded[lane.lane_id] && (
                  <div style={{ marginLeft: 28, marginTop: 6 }}>
                    {cages.filter(c => Number(c.parent_lane_id) === lane.lane_id).map(cage => (
                      <div key={cage.lane_id} style={{ display: "flex", alignItems: "center", gap: 10,
                                                        padding: "6px 10px", borderRadius: 8,
                                                        background: T.inputBg, marginBottom: 4 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: cage.color }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT }}>
                          {cage.lane_name}
                        </span>
                        {chip(cage)}
                        {conflictNames(cage)}
                        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                          <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                           background: T.tabInBg, color: T.tabInClr }}
                            onClick={() => startEdit(cage)}><FaEdit size={10} /></button>
                          <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                           background: "rgba(239,68,68,0.12)", color: "#ef4444" }}
                            onClick={() => deactivate(cage.lane_id)}><FaTrash size={10} /></button>
                        </div>
                      </div>
                    ))}
                    <button style={{ ...S.btn, background: LANE_TYPE_COLORS.cage + "18",
                                     color: LANE_TYPE_COLORS.cage, fontSize: 11, padding: "5px 10px" }}
                      onClick={() => startNew("cage", lane.lane_id)}>
                      <FaPlus size={9} style={{ marginRight: 4 }} />Add Cage
                    </button>
                  </div>
                )}
              </div>
            ))}
            {!fullLanes.length && <div style={{ color: T.textMuted, fontFamily: FONT, fontSize: 13 }}>No full lanes yet.</div>}
          </div>

          {/* Boxes */}
          <div style={{ ...S.card }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: LANE_TYPE_COLORS.box, fontFamily: FONT }}>
                Box Configurations
              </div>
              <button style={{ ...S.btn, background: LANE_TYPE_COLORS.box + "18",
                               color: LANE_TYPE_COLORS.box, fontSize: 12, padding: "6px 12px" }}
                onClick={() => startNew("box")}>
                <FaPlus size={10} style={{ marginRight: 4 }} />Add Box
              </button>
            </div>
            {boxes.map(box => (
              <div key={box.lane_id} style={{ display: "flex", alignItems: "center", gap: 10,
                                              padding: "8px 10px", borderRadius: 8,
                                              background: T.inputBg, marginBottom: 6 }}>
                <div style={{ width: 14, height: 14, borderRadius: 4, background: box.color }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT }}>
                  {box.lane_name}
                </span>
                {chip(box)}
                <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
                  group {box.box_group_id}
                </span>
                {conflictNames(box)}
                <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                   background: T.tabInBg, color: T.tabInClr }}
                    onClick={() => startEdit(box)}><FaEdit size={10} /></button>
                  <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                   background: "rgba(239,68,68,0.12)", color: "#ef4444" }}
                    onClick={() => deactivate(box.lane_id)}><FaTrash size={10} /></button>
                </div>
              </div>
            ))}
            {!boxes.length && <div style={{ color: T.textMuted, fontFamily: FONT, fontSize: 13 }}>No box configs yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Pricing Tab ────────────────────────────────────────────────────────────────
function PricingTab({ T, S }) {
  const [types, setTypes] = useState([]);
  const [form,  setForm]  = useState(null);
  const [saving, setSaving] = useState(false);
  const typesSort = useSort(types, "type_name");

  const load = useCallback(() => {
    fetch(`${BASE}/lb/booking-types`).then(r => r.json()).then(d => setTypes(d.booking_types || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setForm({
    type_name: "", lane_type: "full_lane", duration_label: "1 hour",
    duration_minutes: 60, member_price: 0, non_member_price: 0,
    tax_rate: 0, service_fee: 0, sort_order: 0,
  });

  const startEdit = (t) => setForm({ ...t });

  const save = async () => {
    setSaving(true);
    const url = form.type_id ? `${BASE}/lb/booking-types/${form.type_id}` : `${BASE}/lb/booking-types`;
    const method = form.type_id ? "PUT" : "POST";
    await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSaving(false); setForm(null); load();
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this pricing option?")) return;
    await fetch(`${BASE}/lb/booking-types/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT }}>Pricing Matrix</div>
        <button style={S.yBtn} onClick={startNew}><FaPlus size={12} style={{ marginRight: 6 }} />Add Pricing</button>
      </div>

      {form && (
        <div style={{ ...S.card, marginBottom: 20, borderColor: "rgba(16,185,129,0.35)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 14 }}>
            {form.type_id ? "Edit Pricing" : "New Pricing Option"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={S.lbl}>Product Name</label>
              <input value={form.type_name} onChange={e => setForm(f => ({ ...f, type_name: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} placeholder="e.g. Cricket Lane" />
            </div>
            <div>
              <label style={S.lbl}>Lane Type</label>
              <select value={form.lane_type} onChange={e => setForm(f => ({ ...f, lane_type: e.target.value }))}
                style={{ ...S.inp, width: "100%" }}>
                {Object.entries(LANE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Duration</label>
              <select value={form.duration_minutes}
                onChange={e => {
                  const d = DURATIONS.find(x => x.minutes === Number(e.target.value));
                  setForm(f => ({ ...f, duration_minutes: Number(e.target.value), duration_label: d?.label || "" }));
                }}
                style={{ ...S.inp, width: "100%" }}>
                {DURATIONS.map(d => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            {[["Member Price ($)", "member_price"], ["Non-Member Price ($)", "non_member_price"],
              ["Tax Rate (%)", "tax_rate"], ["Service Fee ($)", "service_fee"]].map(([lbl, key]) => (
              <div key={key}>
                <label style={S.lbl}>{lbl}</label>
                <input type="number" step="0.01" value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: parseFloat(e.target.value) || 0 }))}
                  style={{ ...S.inp, width: "100%" }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.yBtn} onClick={save} disabled={saving}>
              <FaSave size={12} style={{ marginRight: 6 }} />{saving ? "Saving…" : "Save"}
            </button>
            <button style={{ ...S.btn, background: T.tabInBg, color: T.tabInClr }}
              onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {[
                { label: "Product", key: "type_name" },
                { label: "Type", key: "lane_type" },
                { label: "Duration", key: "duration_label" },
                { label: "Member $", key: "member_price" },
                { label: "Non-Member $", key: "non_member_price" },
                { label: "Tax %", key: "tax_rate" },
                { label: "Fee $", key: "service_fee" },
                { label: "", key: null },
              ].map(({ label: h, key }, i) => (
                <th key={i}
                  onClick={key ? () => typesSort.toggleSort(key) : undefined}
                  style={{ padding: "10px 14px", fontSize: 11, fontWeight: 800, color: T.textLabel,
                                     fontFamily: FONT, textTransform: "uppercase", textAlign: "left",
                                     borderBottom: `1px solid ${T.divider}`,
                                     ...(key ? { cursor: "pointer", userSelect: "none" } : {}),
                                     ...(key && typesSort.sortKey === key ? { color: getBrandColors().blue } : {}) }}>
                  {h}{key ? typesSort.sortArrow(key) : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {typesSort.sorted.map(t => (
              <tr key={t.type_id}>
                <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 700, color: T.text,
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>{t.type_name}</td>
                <td style={{ padding: "11px 14px", fontSize: 12, color: LANE_TYPE_COLORS[t.lane_type],
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>
                  {LANE_TYPE_LABELS[t.lane_type]}
                </td>
                <td style={{ padding: "11px 14px", fontSize: 13, color: T.text,
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>{t.duration_label}</td>
                <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 700, color: "#10b981",
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>
                  ${parseFloat(t.member_price).toFixed(2)}
                </td>
                <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 700, color: "#f59e0b",
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>
                  ${parseFloat(t.non_member_price).toFixed(2)}
                </td>
                <td style={{ padding: "11px 14px", fontSize: 13, color: T.textMuted,
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>{t.tax_rate}%</td>
                <td style={{ padding: "11px 14px", fontSize: 13, color: T.textMuted,
                             fontFamily: FONT, borderBottom: `1px solid ${T.divider}` }}>
                  ${parseFloat(t.service_fee).toFixed(2)}
                </td>
                <td style={{ padding: "11px 14px", borderBottom: `1px solid ${T.divider}` }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                     background: T.tabInBg, color: T.tabInClr }}
                      onClick={() => startEdit(t)}><FaEdit size={10} /></button>
                    <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                     background: "rgba(239,68,68,0.12)", color: "#ef4444" }}
                      onClick={() => deactivate(t.type_id)}><FaTrash size={10} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!types.length && (
          <div style={{ textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 13, padding: 40 }}>
            No pricing options yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Block-outs Tab ─────────────────────────────────────────────────────────────
const WEEK_DAYS = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" }, { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];
const STATUS_COLORS_BO = { maintenance: "#f59e0b", holiday: "#6366f1",
                            academy_reserve: "#10b981", closure: "#ef4444", other: "#8b5cf6" };

function parseDays(json) {
  try { return JSON.parse(json || "[]") || []; } catch { return []; }
}

function BlockOutsTab({ T, S }) {
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const [facilities, setFacilities] = useState([]);
  const [lanes,      setLanes]      = useState([]);
  const [blockOuts,  setBlockOuts]  = useState([]);
  const [selFac,     setSelFac]     = useState(null);
  const [form,       setForm]       = useState(null);
  const [saving,     setSaving]     = useState(false);

  // Load facilities once on mount only
  useEffect(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json()).then(d => {
      const list = d.facilities || [];
      setFacilities(list);
      if (list.length) setSelFac(fac => fac || list[0].facility_id);
    });
  }, []);

  // Load lanes + block-outs whenever facility changes
  const loadBlockOuts = useCallback((fid) => {
    if (!fid) return;
    fetch(`${BASE}/lb/block-outs?facility_id=${fid}`).then(r => r.json())
      .then(d => setBlockOuts(d.block_outs || []));
  }, []);

  useEffect(() => {
    if (!selFac) return;
    fetch(`${BASE}/lb/lanes?facility_id=${selFac}`).then(r => r.json())
      .then(d => setLanes(d.lanes || []));
    loadBlockOuts(selFac);
  }, [selFac, loadBlockOuts]);

  const emptyForm = () => ({
    facility_id: selFac, lane_id: null, block_type: "maintenance",
    title: "", start_date: "", end_date: "", start_time: "", end_time: "",
    is_recurring: 0, recur_pattern: null, blocked_days_json: "[]",
    notes: "", created_by: "admin",
  });

  const startNew  = () => setForm(emptyForm());
  const startEdit = (b) => setForm({
    ...b,
    start_time: b.start_time || "",
    end_time:   b.end_time   || "",
    blocked_days_json: b.blocked_days_json || "[]",
  });

  const toggleDay = (key) => {
    const days = parseDays(form.blocked_days_json);
    const next = days.includes(key) ? days.filter(d => d !== key) : [...days, key];
    setForm(f => ({ ...f, blocked_days_json: JSON.stringify(next) }));
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      start_time: form.start_time || null,
      end_time:   form.end_time   || null,
    };
    const url    = form.blockout_id ? `${BASE}/lb/block-outs/${form.blockout_id}` : `${BASE}/lb/block-outs`;
    const method = form.blockout_id ? "PUT" : "POST";
    await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    setSaving(false);
    setForm(null);
    loadBlockOuts(selFac);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this block-out?")) return;
    await fetch(`${BASE}/lb/block-outs/${id}`, { method: "DELETE" });
    setBlockOuts(b => b.filter(x => x.blockout_id !== id));
  };

  const laneName = (id) => { const l = lanes.find(x => x.lane_id === Number(id)); return l ? l.lane_name : `Lane ${id}`; };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <label style={S.lbl}>Facility</label>
          <select value={selFac || ""} onChange={e => setSelFac(Number(e.target.value))}
            style={{ ...S.inp, width: 260 }}>
            {facilities.map(f => <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>)}
          </select>
        </div>
        <button style={S.yBtn} onClick={startNew}><FaPlus size={12} style={{ marginRight: 6 }} />Add Block-out</button>
      </div>

      {form && (
        <div style={{ ...S.card, marginBottom: 20, borderColor: "rgba(239,68,68,0.3)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FONT, marginBottom: 14 }}>
            {form.blockout_id ? "Edit Block-out" : "New Block-out"}
          </div>

          {/* Row 1: Type / Title / Lane */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={S.lbl}>Type</label>
              <select value={form.block_type} onChange={e => setForm(f => ({ ...f, block_type: e.target.value }))}
                style={{ ...S.inp, width: "100%" }}>
                {BLOCK_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Title / Description</label>
              <input value={form.title || ""} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} placeholder="e.g. Public Holiday" />
            </div>
            <div>
              <label style={S.lbl}>Lane (blank = entire facility)</label>
              <select value={form.lane_id || ""} onChange={e => setForm(f => ({ ...f, lane_id: Number(e.target.value) || null }))}
                style={{ ...S.inp, width: "100%" }}>
                <option value="">Entire Facility</option>
                {lanes.map(l => <option key={l.lane_id} value={l.lane_id}>{l.lane_name}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Date range */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={S.lbl}>Start Date</label>
              <input type="date" value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
            <div>
              <label style={S.lbl}>End Date</label>
              <input type="date" value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
            <div>
              <label style={S.lbl}>Start Time (blank = full day)</label>
              <input type="time" value={form.start_time || ""}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
            <div>
              <label style={S.lbl}>End Time</label>
              <input type="time" value={form.end_time || ""}
                onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
          </div>

          {/* Days of week */}
          <div style={{ marginBottom: 14 }}>
            <label style={S.lbl}>Blocked Days of Week (select days within the date range to block)</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {WEEK_DAYS.map(({ key, label }) => {
                const active = parseDays(form.blocked_days_json).includes(key);
                return (
                  <button key={key} type="button"
                    onClick={() => toggleDay(key)}
                    style={{ padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                             fontSize: 12, fontWeight: 700, fontFamily: FONT,
                             background: active ? "#ef4444" : T.inputBg,
                             color: active ? "#fff" : T.textMuted,
                             outline: active ? "2px solid #ef444488" : "none" }}>
                    {label}
                  </button>
                );
              })}
              <span style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, alignSelf: "center", marginLeft: 4 }}>
                {parseDays(form.blocked_days_json).length === 0
                  ? "No specific days selected — entire date range is blocked"
                  : `Blocks only on selected days within the date range`}
              </span>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={S.lbl}>Notes</label>
            <textarea value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              style={{ ...S.inp, width: "100%", minHeight: 60, resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.yBtn} onClick={save} disabled={saving}>
              <FaSave size={12} style={{ marginRight: 6 }} />{saving ? "Saving…" : "Save"}
            </button>
            <button style={{ ...S.btn, background: T.tabInBg, color: T.tabInClr }}
              onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Block-out list */}
      {blockOuts.map(b => {
        const days    = parseDays(b.blocked_days_json);
        const color   = STATUS_COLORS_BO[b.block_type] || "#8b5cf6";
        return (
          <div key={b.blockout_id} style={{ ...S.card, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%",
                              background: color, marginTop: 4, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT }}>
                    {b.title || b.block_type.replace(/_/g, " ")}
                    <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 8, padding: "2px 8px",
                                   borderRadius: 12, background: `${color}22`, color }}>
                      {b.block_type.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 3 }}>
                    {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                    {b.start_time ? ` · ${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}` : " · Full day"}
                    {b.lane_id ? ` · ${laneName(b.lane_id)}` : " · Entire facility"}
                  </div>
                  {/* Day pills */}
                  {days.length > 0 && (
                    <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: T.textLabel, fontFamily: FONT, alignSelf: "center" }}>
                        Days blocked:
                      </span>
                      {WEEK_DAYS.filter(d => days.includes(d.key)).map(d => (
                        <span key={d.key} style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px",
                                                   borderRadius: 8, background: "#ef444422",
                                                   color: "#ef4444", fontFamily: FONT }}>
                          {d.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {days.length === 0 && (
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 4 }}>
                      All days in range blocked
                    </div>
                  )}
                  {b.notes && (
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 4,
                                  fontStyle: "italic" }}>{b.notes}</div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                 background: T.tabInBg, color: T.tabInClr }}
                  onClick={() => startEdit(b)}><FaEdit size={10} /></button>
                <button style={{ ...S.btn, padding: "4px 10px", fontSize: 11,
                                 background: "rgba(239,68,68,0.12)", color: "#ef4444" }}
                  onClick={() => remove(b.blockout_id)}><FaTrash size={10} /></button>
              </div>
            </div>
          </div>
        );
      })}
      {!blockOuts.length && !form && (
        <div style={{ ...S.card, textAlign: "center", color: T.textMuted, fontFamily: FONT, padding: 40 }}>
          No block-outs defined for this facility.
        </div>
      )}
    </div>
  );
}

// ── Membership Plans Tab ───────────────────────────────────────────────────────
function MembershipPlansTab({ T, S }) {
  const [plans,  setPlans]  = useState([]);
  const [form,   setForm]   = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch(`${BASE}/lb/membership-plans`).then(r => r.json()).then(d => setPlans(d.plans || []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const url    = form.plan_id ? `${BASE}/lb/membership-plans/${form.plan_id}` : `${BASE}/lb/membership-plans`;
    const method = form.plan_id ? "PUT" : "POST";
    await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSaving(false); setForm(null); load();
  };

  const blank = { plan_name: "", description: "", duration_months: 1, price: 0, hours_per_month: 0, billing_frequency: "monthly" };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: FONT }}>Membership Plans</div>
        <button style={{ ...S.btn, background: "rgba(99,102,241,0.12)", color: "#6366f1", fontSize: 12, padding: "7px 14px" }}
          onClick={() => setForm({ ...blank })}>
          <FaPlus size={10} style={{ marginRight: 6 }} />Add Plan
        </button>
      </div>

      {form && (
        <div style={{ background: T.inputBg, border: `1.5px solid #6366f1`, borderRadius: 12,
                      padding: "16px 18px", marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6366f1", fontFamily: FONT,
                        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
            {form.plan_id ? "Edit Plan" : "New Plan"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={S.lbl}>Plan Name</label>
              <input value={form.plan_name} onChange={e => setForm(f => ({ ...f, plan_name: e.target.value }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
            <div>
              <label style={S.lbl}>Duration (months)</label>
              <input type="number" min="1" value={form.duration_months}
                onChange={e => setForm(f => ({ ...f, duration_months: Number(e.target.value) }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
            <div>
              <label style={S.lbl}>Price ($)</label>
              <input type="number" step="0.01" min="0" value={form.price}
                onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
            <div>
              <label style={S.lbl}>Hours / Month</label>
              <input type="number" step="0.5" min="0" placeholder="0 = unlimited" value={form.hours_per_month || 0}
                onChange={e => setForm(f => ({ ...f, hours_per_month: parseFloat(e.target.value) || 0 }))}
                style={{ ...S.inp, width: "100%" }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={S.lbl}>Description</label>
            <input value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              style={{ ...S.inp, width: "100%" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.yBtn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Plan"}</button>
            <button style={{ ...S.btn, background: T.tabInBg, color: T.tabInClr }} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
        {plans.map(p => (
          <div key={p.plan_id}
            style={{ padding: "14px 16px", borderRadius: 12, background: T.inputBg,
                     border: T.glassBdr, cursor: "pointer", transition: "box-shadow 0.15s" }}
            onClick={() => setForm({ ...p, hours_per_month: p.hours_per_month || 0 })}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.18)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <FaMedal size={14} color="#6366f1" />
              <span style={{ fontSize: 14, fontWeight: 800, color: "#6366f1", fontFamily: FONT }}>{p.plan_name}</span>
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>
              ${p.price} / {p.duration_months} month{p.duration_months > 1 ? "s" : ""}
            </div>
            {parseFloat(p.hours_per_month || 0) > 0 && (
              <div style={{ fontSize: 11, color: "#10b981", fontFamily: FONT, marginTop: 4, fontWeight: 700 }}>
                {p.hours_per_month} hrs/mo included
              </div>
            )}
            {p.description && (
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 4 }}>{p.description}</div>
            )}
          </div>
        ))}
        {!plans.length && (
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, gridColumn: "1/-1", padding: 20 }}>
            No membership plans yet. Click "Add Plan" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────
export default function LaneBookingConfig() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const S = makeS(T);
  const [tab, setTab] = useState("facilities");

  const TABS = [
    { key: "facilities",   label: "Facilities",        icon: FaWarehouse },
    { key: "lanes",        label: "Lanes",             icon: FaLayerGroup },
    { key: "pricing",      label: "Pricing",           icon: FaTag },
    { key: "blockouts",    label: "Block-outs",        icon: FaCalendarTimes },
    { key: "memberships",  label: "Membership Plans",  icon: FaMedal },
  ];

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1000, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaWarehouse size={20} color={getBrandColors().blueDark} />} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>Facility Configuration</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Set up facilities, lane hierarchy, pricing, and block-out periods
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} style={S.tab(tab === key)} onClick={() => setTab(key)}>
            <Icon size={11} style={{ marginRight: 6 }} />{label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === "facilities" && <FacilitiesTab T={T} S={S} />}
      {tab === "lanes"      && <LanesTab T={T} S={S} />}
      {tab === "pricing"     && <PricingTab T={T} S={S} />}
      {tab === "blockouts"   && <BlockOutsTab T={T} S={S} />}
      {tab === "memberships" && <MembershipPlansTab T={T} S={S} />}
    </div>
  );
}
